<script>
import helpers from "@/packages/cassandra-base/utilities/helpers/helper.js";
import PageHeader from "../../components/PageHeader.vue";
import ApprovalEntries from "../../components/ApprovalEntries.vue";
import RecallForm from "../../components/recalls/RecallForm.vue";

export default {
  name: "Show",
  components: { RecallForm, ApprovalEntries, PageHeader },
  data: function () {
    return {
      showForm: false,
    };
  },
  mounted() {
    if (this.$route.params.no) {
      this.$store.dispatch("leaveRecall/getRecall", this.$route.params.no);
    }
  },

  computed: {
    recall: function () {
      return this.$store.getters["leaveRecall/leaveRecallGetters"]("recall");
    },

    approvalEntries: function () {
      return this.recall?.approvalEntries || [];
    },

    userData: function () {
      return this.$store.getters["auth/user"];
    },

    profile: function () {
      return this.userData.profile || {};
    },

    isOpen() {
      return this.recall.status === "Open";
    },

    isRequester() {
      return this.recall.requestedByEmployeeNo === this.userData.employee;
    },

    isBeingRecalled() {
      return this.recall.employeeNo === this.userData.employee;
    },

    canEdit() {
      return this.isRequester && this.isOpen;
    },

    canSubmit() {
      return this.isRequester && !this.recall.submit;
    },

    canSendForApproval() {
      return this.isRequester && this.recall.recallAccepted && this.isOpen;
    },

    canCancelApprovalRequest() {
      return this.isRequester && this.recall.status === "Pending Approval";
    },

    canAcceptOrReject() {
      return (
        this.isBeingRecalled &&
        this.recall.submit &&
        !this.recall.recallAccepted &&
        !this.recall.recallRejected
      );
    },
  },

  methods: {
    goBack: function () {
      this.$router.back();
    },

    submitRecall: function () {
      this.$confirm.show({
        title: "Submit Leave Recall",
        text: `Leave Recall ${this.recall.recallNo} will be marked as submitted. Do you want to continue?`,
        onConfirm: () => {
          this.$store.dispatch("leaveRecall/submitRecall", this.recall);
        },
      });
    },

    sendForApproval: function () {
      this.$confirm.show({
        title: "Send For Approval",
        text: `Leave Recall ${this.recall.recallNo} will be sent for approval. Do you want to continue?`,
        onConfirm: () => {
          this.$store.dispatch("leaveRecall/sendForApproval", this.recall);
        },
      });
    },

    reopenRecall: function () {
      this.$confirm.show({
        title: "Cancal Approval Request",
        text: `Leave Recall ${this.recall.recallNo} approval request will be canceled. Do you want to continue?`,
        onConfirm: () => {
          this.$store.dispatch("leaveRecall/reopenRecall", this.recall);
        },
      });
    },

    acceptRecall: function () {
      this.$confirm.show({
        title: "Accept Leave Recall",
        text: `Leave Recall ${this.recall.recallNo} will be marked as accepted. Do you want to continue?`,
        onConfirm: () => {
          this.$store.dispatch("leaveRecall/acceptRecall", this.recall);
        },
      });
    },

    rejectRecall: function () {
      this.$confirm.show({
        title: "Reject Leave Recall",
        text: `Leave Recall ${this.recall.recallNo} will be marked as rejected. Do you want to continue?`,
        onConfirm: () => {
          this.$store.dispatch("leaveRecall/denyRecall", this.recall);
        },
      });
    },

    deleteRecall: function () {
      this.$confirm.show({
        title: "Delete Leave Recall",
        text: `Leave Recall ${this.recall.recallNo} will be deleted. Do you want to continue?`,
        onConfirm: () => {
          this.$store
            .dispatch("leaveRecall/deleteRecall", this.recall)
            .then(() => {
              this.goBack();
            })
            .catch((error) => {
              console.log(
                "Error encountered while deleting recall",
                error.response
              );
            });
        },
      });
    },

    editRecord() {
      this.showForm = true;
    },

    closeForm() {
      this.showForm = false;
    },

    getColor: function (status) {
      return helpers.getColor(status);
    },
  },
};
</script>

<template>
  <v-container>
    <page-header
      title="View Application"
      :path="$router.currentRoute.meta.breadcrumb"
    />

    <v-card elevation="0" v-if="recall">
      <v-card class="d-flex" flat>
        <v-card class="mr-auto my-2 mx-2" flat>
          <div class="d-flex justify-start">
            <span class="text-lg-h6">Recall No: </span>
            <div class="mx-2 pt-1">
              <v-chip color="primary" label>
                {{ recall.recallNo }}
              </v-chip>
            </div>
            <div class="mx-2 pt-1">
              <v-chip label :color="getColor(recall.status).color" small>
                {{ recall.status }}
              </v-chip>
            </div>
          </div>
        </v-card>

        <v-card class="pa-2 d-flex justify-end" flat>
          <div class="mr-2">
            <v-btn class="btnActions" @click="goBack()">
              <v-icon class="mx-3">arrow_back</v-icon>
              go back
            </v-btn>
          </div>

          <div v-if="canEdit" class="mr-2">
            <v-btn
              color="primary"
              class="btnActions"
              @click.prevent="editRecord"
            >
              <v-icon class="mx-3">mode_edit</v-icon>
              Edit
            </v-btn>
          </div>

          <template v-if="canSubmit">
            <div class="mr-2">
              <v-btn
                color="success"
                class="btnActions"
                @click.prevent="submitRecall"
              >
                <v-icon class="mx-3">cloud_upload</v-icon>
                Submit
              </v-btn>
            </div>

            <div class="mr-2">
              <v-btn
                color="error"
                class="btnActions"
                @click.prevent="deleteRecall"
              >
                <v-icon class="mx-3">mdi-delete</v-icon>
                Delete
              </v-btn>
            </div>
          </template>

          <div v-if="canSendForApproval">
            <v-btn
              color="success"
              class="btnActions"
              @click.prevent="sendForApproval"
            >
              <v-icon class="mx-3">cloud_upload</v-icon>
              Send for Approval
            </v-btn>
          </div>

          <div v-if="canCancelApprovalRequest">
            <v-btn
              color="error"
              class="btnActions"
              @click.prevent="reopenRecall"
            >
              <v-icon class="mx-3">mdi-cancel</v-icon>
              Cancel Approval
            </v-btn>
          </div>

          <template v-if="canAcceptOrReject">
            <div class="mr-2">
              <v-btn
                color="success"
                class="btnActions"
                @click.prevent="acceptRecall"
              >
                <v-icon class="mx-3">mdi-thumb-up</v-icon>
                Accept
              </v-btn>
            </div>
            <div>
              <v-btn
                color="error"
                class="btnActions"
                @click.prevent="rejectRecall"
              >
                <v-icon class="mx-3">mdi-thumb-down</v-icon>
                Reject
              </v-btn>
            </div>
          </template>
        </v-card>
      </v-card>
      <v-divider></v-divider>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-card flat>
              <v-card-title class=""> Employee </v-card-title>
              <v-card-text>
                <div class="d-flex flex-column flex-sm-row justify-start">
                  <div>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title> Employee </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ recall.employeeName }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </div>
                  <div>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title> Employee Number </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ recall.employeeNo }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </div>

                  <div>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title> Requested By </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ recall.requestedByEmployeeName }} ({{
                            recall.requestedByEmployeeNo
                          }})
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </div>
                </div>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title> Recall Reason </v-list-item-title>
                    <v-list-item-subtitle class="text-wrap">
                      {{ recall.reason }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card flat>
              <v-card-title>
                Recall days
                <v-chip color="primary" light small class="ml-2">
                  {{ recall.recallDays }}
                </v-chip>
              </v-card-title>
              <v-card-text>
                <div class="d-flex flex-column flex-sm-row justify-start">
                  <div>
                    <v-list-item>
                      <v-list-item-avatar
                        color="light-blue lighten-4"
                        size="50"
                      >
                        <v-icon color="light-blue darken-4">date_range</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title> Return Date </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ recall.returnDate | formatDate }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <template v-if="approvalEntries.length">
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <ApprovalEntries :approvalEntries="approvalEntries" class="mt-3" />
          </v-row>
        </v-card-text>
      </template>
    </v-card>

    <RecallForm
      :show.sync="showForm"
      :employee-no="profile.number"
      :record="recall"
      @close="closeForm"
    />
  </v-container>
</template>

<style scoped></style>
